export interface MergedEdits<T> {
  merged: T;
  conflict: boolean;
}

// Three-way merge for an open component, step or container whose saved copy changed outside the editor: starts
// from the reloaded copy and re-applies what the user changed since opening it. Where both changed the same value,
// the reloaded copy wins and `conflict` is set. Arrays count as one value. `$type` always comes from the reloaded
// copy, since saving rewrites it on the working copy.
export function mergeUnsavedEdits<T>(base: T, edited: T, reloaded: T): MergedEdits<T> {
  let conflict = false;

  const merge = (baseValue: unknown, editedValue: unknown, reloadedValue: unknown): unknown => {
    if (isEqual(editedValue, baseValue)) {
      return reloadedValue;
    }
    if (isEqual(reloadedValue, baseValue) || isEqual(reloadedValue, editedValue)) {
      return editedValue;
    }
    if (isPlainObject(baseValue) && isPlainObject(editedValue) && isPlainObject(reloadedValue)) {
      const result: Record<string, unknown> = {};
      const keys = new Set([...Object.keys(baseValue), ...Object.keys(editedValue), ...Object.keys(reloadedValue)]);
      for (const key of keys) {
        const value =
          key === '$type' ? reloadedValue[key] : merge(baseValue[key], editedValue[key], reloadedValue[key]);
        if (value !== undefined) {
          result[key] = value;
        }
      }
      return result;
    }
    conflict = true;
    return reloadedValue;
  };

  const merged = merge(base, edited, reloaded) as T;
  return { merged: JSON.parse(JSON.stringify(merged)), conflict };
}

function isPlainObject(value: unknown): value is Record<string, unknown> {
  return value !== null && typeof value === 'object' && !Array.isArray(value);
}

function isEqual(a: unknown, b: unknown): boolean {
  return JSON.stringify(a) === JSON.stringify(b);
}
